import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * Button — 4 varianty (primary, secondary, ghost, link), 3 velikosti.
 */

type Variant = "primary" | "secondary" | "ghost" | "link";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-2 font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none";

const variants: Record<Variant, string> = {
  primary:
    "bg-ktm-orange text-white hover:bg-ktm-orange-600 active:bg-ktm-orange-700 shadow-sm",
  secondary:
    "bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 active:bg-zinc-100 shadow-sm",
  ghost: "text-zinc-700 hover:bg-zinc-100 active:bg-zinc-200",
  link: "text-ktm-orange hover:underline p-0 h-auto",
};

const sizes: Record<Size, string> = {
  // ⚠ Na mobilu 44 px — doporučená klikací plocha prstem. `sm` se používá
  // hlavně ve stránkování, kde se 32px tlačítko na telefonu špatně trefuje.
  // Na počítači zůstává 32 px, aby se vzhled nezměnil.
  sm: "h-11 sm:h-8 px-3 text-sm rounded-md",
  md: "h-11 sm:h-10 px-4 text-sm rounded-lg",
  lg: "h-12 px-6 text-base rounded-lg",
};

export function Button({
  variant = "primary",
  size = "md",
  iconLeft,
  iconRight,
  fullWidth,
  className = "",
  children,
  ...rest
}: ButtonProps): JSX.Element {
  const sizeClass = variant === "link" ? "" : sizes[size];
  return (
    <button
      className={`${base} ${variants[variant]} ${sizeClass} ${
        fullWidth ? "w-full" : ""
      } ${className}`}
      {...rest}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}
